import { FormattedMessage } from 'react-intl';
import { Divider, Icon, Dropdown } from '@ets/design-system';
import EditorManager from 'Editor/services/EditorManager';
import { selectReadOnlyMode } from 'Editor/redux/EditorStatusSlice';
import { selectDocumentStyles } from 'Editor/redux/StylesSlice';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { useSelector, useDispatch } from '_common/hooks';
import DocumentStylePreview from 'Editor/components/DocumentStylePreview/DocumentStylePreview';
import styles from './ContentStructure.module.scss';
import { useEffect, useState } from 'react';
import { completeAction, resetAction } from 'App/redux/onboardingSlice';

const ContentStructure = () => {
  const dispatch = useDispatch();
  const documentStyles = useSelector((state) => selectDocumentStyles(state));
  const selectedStyle = useSelector((state) => state.editor.status.selectedStyle);
  const isReadOnlyMode = useSelector((state) => selectReadOnlyMode(state));
  const [isOpen, setIsOpen] = useState(false);
  const disabled = isReadOnlyMode;

  useEffect(() => {
    if (isOpen) {
      dispatch(completeAction('editor_writing_heading_open'));
    } else {
      dispatch(resetAction('editor_writing_heading_open'));
    }
  }, [isOpen]);

  const openRenameModal = (name: string) => {
    dispatch(
      openAndUpdateModal({
        modal: 'RenameDocumentStylesModal',
        data: {
          isOpen: true,
          styleId: selectedStyle?.id,
          name,
        },
      }),
    );
  };

  const applyToSelection = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    styleId: string,
  ) => {
    dispatch(completeAction('editor_writing_heading_select'));
    const manager = EditorManager.getInstance();
    if (manager) {
      manager.applyDocumentStyleToSelection({ style: styleId });
    }
  };

  const updateStyleFromSelection = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    styleId: string,
  ) => {
    dispatch(completeAction('editor_writing_heading_select'));
    const manager = EditorManager.getInstance();
    if (manager) {
      manager.updateDocumentStyleFromSelection(styleId);
    }
  };

  const { data, list } = documentStyles;
  return (
    <Dropdown.Root
      open={isOpen}
      onOpenChange={() => {
        if (!disabled) setIsOpen(!isOpen);
      }}
    >
      <Dropdown.Trigger>
        <div
          className={`${styles.toggleDropdown} ${disabled && styles.disabled}`}
          data-testid="content-structure"
          id="headingSelect"
        >
          <div className={styles.text}>
            {`${selectedStyle && data && data[selectedStyle.id] ? data[selectedStyle.id].alias : ''}${
              selectedStyle && selectedStyle.changed ? '*' : ''
            }`}
          </div>
          <div className={styles.arrowDiv}>
            <Icon icon="chevron-down" size="2" testId="content-structure" />
          </div>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Content testId="content-structure-dropdown">
        {list &&
          list.slice(0, 5).map((styleId: string, index: number) => (
            <div key={data[styleId].n}>
              {index > 0 && <Divider margin="0" />}
              <Dropdown.Sub>
                <Dropdown.SubTrigger
                  onClick={(event) => {
                    applyToSelection(event, styleId);
                    setIsOpen(false);
                  }}
                  testId="style-dropdown-submenu"
                  id={styleId === 'h3' ? 'headingTargetAction' : undefined}
                >
                  <DocumentStylePreview id={styleId} style={data[styleId]}>
                    {data[styleId].alias}
                  </DocumentStylePreview>
                </Dropdown.SubTrigger>
                <Dropdown.SubContent testId="style-dropdown-submenu-content">
                  <Dropdown.Item
                    onClick={(event) => applyToSelection(event, styleId)}
                    testId="apply-dropdown-item"
                  >
                    <FormattedMessage id="APPLY" values={{ name: data[styleId].alias }} />
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={(event) => updateStyleFromSelection(event, styleId)}
                    testId="apply-from-selection-dropdown-item"
                  >
                    <FormattedMessage id="UPDATE_FROM_SELECTION" />
                  </Dropdown.Item>
                  <Divider margin="0.25rem 0 0.25rem 0" />
                  <Dropdown.Item
                    onClick={() => openRenameModal(data[styleId].n)}
                    testId="rename-style-dropdown-item"
                  >
                    <FormattedMessage id="RENAME_STYLE" />
                  </Dropdown.Item>
                </Dropdown.SubContent>
              </Dropdown.Sub>
            </div>
          ))}
      </Dropdown.Content>
    </Dropdown.Root>
  );
};

export default ContentStructure;
